import { PremiumRentalVerificationSurveyActivity } from "../types";
import { Show } from "shared/components";
import { getApplicantRole } from "utils/getApplicantRole";
import { getFullName } from "shared/utils/get-full-name";
import { RoleTitles } from "views/settings-users/slices/users/constants";

interface Props {
  activityItem: PremiumRentalVerificationSurveyActivity;
}

interface AuthorLineProps {
  author?: PremiumRentalVerificationSurveyActivity["author"];
}

const AuthorLine = ({ author }: AuthorLineProps) => {
  if (!author?.name) return null;
  const role = author.author_role ? RoleTitles[author.author_role] : null;
  return (
    <p className="text-sm font-normal text-content-400">
      by {author.name}
      {role ? `, ${role}` : ""}
    </p>
  );
};

type SurveySentActivity = Extract<
  PremiumRentalVerificationSurveyActivity,
  {
    activity_type:
      | "premium_rental_history_survey_sent"
      | "premium_rental_history_survey_resent"
      | "premium_rental_history_survey_manually_sent";
  }
>;

const SurveySentView = ({
  activityItem
}: {
  activityItem: SurveySentActivity;
}) => {
  const { details, activity_type, author } = activityItem;
  const landlordName = details.landlord_details?.name ?? "";
  const landlordEmail = details.landlord_details?.email ?? "";
  const applicantName = details.contact
    ? getFullName({
        firstName: details.contact.first_name,
        lastName: details.contact.last_name,
        middleName: details.contact.middle_name
      })
    : "";
  const applicantRole = getApplicantRole(details.contact?.role);
  const isResent = activity_type === "premium_rental_history_survey_resent";
  const isManual =
    activity_type === "premium_rental_history_survey_manually_sent";

  return (
    <div className="mt-2">
      <p className="text-sm font-normal text-content-400">
        {landlordName} {landlordEmail ? `(${landlordEmail})` : ""} was emailed a
        survey to verify {applicantName}
        {applicantRole ? ` (${applicantRole})` : ""}&apos;s rental history.
      </p>
      <Show if={isResent && typeof details.counter === "number"}>
        <p className="text-sm font-normal text-content-400">
          Reminder {details.counter} of {details.max_counter ?? "—"}
        </p>
      </Show>
      <Show if={isManual}>
        <AuthorLine author={author} />
      </Show>
    </div>
  );
};

type LandlordDetailsUpdatedActivity = Extract<
  PremiumRentalVerificationSurveyActivity,
  { activity_type: "premium_rental_history_survey_landlord_details_updated" }
>;

const LandlordDetailsUpdatedView = ({
  activityItem
}: {
  activityItem: LandlordDetailsUpdatedActivity;
}) => {
  const { details, author } = activityItem;
  const prev = details.original_landlord_details ?? {};
  const next = details.updated_landlord_details ?? {};
  const changedName = Boolean(next.name && next.name !== prev.name);
  const changedEmail = Boolean(next.email && next.email !== prev.email);
  const changedPhone = Boolean(next.phone && next.phone !== prev.phone);

  return (
    <div className="mt-2">
      <Show if={changedName}>
        <p className="text-sm font-normal text-content-400">
          Previous name {prev.name ?? "—"} → New name {next.name}
        </p>
      </Show>
      <Show if={changedEmail}>
        <p className="text-sm font-normal text-content-400">
          Previous email {prev.email ?? "—"} → New email {next.email}
        </p>
      </Show>
      <Show if={changedPhone}>
        <p className="text-sm font-normal text-content-400">
          Previous phone {prev.phone ?? "—"} → New phone {next.phone}
        </p>
      </Show>
      <AuthorLine author={author} />
      <Show if={details.reason}>
        <p className="text-sm font-normal text-content-400">
          Reason: {details.reason}
        </p>
      </Show>
    </div>
  );
};

type LogOutreachActivity = Extract<
  PremiumRentalVerificationSurveyActivity,
  { activity_type: "premium_rental_history_survey_log_outreach" }
>;

const LogOutreachView = ({
  activityItem
}: {
  activityItem: LogOutreachActivity;
}) => {
  const { details, author } = activityItem;
  const rawContact = (details as { contact?: unknown }).contact;
  const contact = typeof rawContact === "string" ? rawContact : null;
  return (
    <div className="mt-2">
      <Show if={contact}>
        <p className="text-sm font-normal text-content-400">
          Contact: {contact}
        </p>
      </Show>
      <Show if={details.note}>
        <p className="text-sm font-normal text-content-400">
          Note: {details.note}
        </p>
      </Show>
      <AuthorLine author={author} />
    </div>
  );
};

type DeclinedActivity = Extract<
  PremiumRentalVerificationSurveyActivity,
  { activity_type: "premium_rental_history_survey_declined" }
>;

const DeclinedView = ({ activityItem }: { activityItem: DeclinedActivity }) => {
  const { details } = activityItem;
  const name = details.landlord_name?.trim();
  const email = details.landlord_email?.trim();
  const subject =
    name && email ? `${name} (${email})` : name || email || "The landlord";
  return (
    <div className="mt-2">
      <p className="text-sm font-bold text-content-400">
        {subject} declined the survey.
      </p>
      <Show if={details.decline_reason}>
        <p className="text-sm font-normal text-content-400">
          Reason: {details.decline_reason}
        </p>
      </Show>
    </div>
  );
};

type CaseClosedActivity = Extract<
  PremiumRentalVerificationSurveyActivity,
  { activity_type: "premium_rental_history_survey_case_closed" }
>;

const CaseClosedView = ({
  activityItem
}: {
  activityItem: CaseClosedActivity;
}) => {
  const { details, author } = activityItem;
  return (
    <div className="mt-2">
      <Show if={details.reason}>
        <p className="text-sm font-normal text-content-400">
          Reason: {details.reason}
        </p>
      </Show>
      <Show if={details.note}>
        <p className="text-sm font-normal text-content-400">
          Note: {details.note}
        </p>
      </Show>
      <AuthorLine author={author} />
    </div>
  );
};

export const PremiumRentalVerificationSurveyView = ({
  activityItem
}: Props) => {
  switch (activityItem.activity_type) {
    case "premium_rental_history_survey_sent":
    case "premium_rental_history_survey_resent":
    case "premium_rental_history_survey_manually_sent":
      return <SurveySentView activityItem={activityItem} />;
    case "premium_rental_history_survey_landlord_details_updated":
      return <LandlordDetailsUpdatedView activityItem={activityItem} />;
    case "premium_rental_history_survey_log_outreach":
      return <LogOutreachView activityItem={activityItem} />;
    case "premium_rental_history_survey_declined":
      return <DeclinedView activityItem={activityItem} />;
    case "premium_rental_history_survey_case_closed":
      return <CaseClosedView activityItem={activityItem} />;
    default:
      return null;
  }
};
